import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { FadeUp, MaskLine, Overline } from "@/components/Reveal";
import { METRICS } from "@/content/site";

const EXPERTISE = [
  {
    id: "customer-experience",
    title: "Customer Experience",
    body: "I've spent years inside the moments where companies either keep people or lose them — improving customer loyalty, retention, satisfaction, service, and communication across email, SMS, and sales-through-service. The through-line: customers are people, and people remember how you made them feel long after they forget what you sold them.",
    items: ["Customer loyalty & retention", "Customer satisfaction", "Customer service transformation", "Email / SMS communication", "Sales through service", "Customer lifetime value"],
    metric: "$30M+ in revenue generated through email and customer engagement · 5X increase in customer lifetime value · 213% increase in customer-service ticket conversion · 115K+ customer interactions managed and transformed.",
  },
  {
    id: "employee-experience-leadership",
    title: "Employee Experience & Leadership",
    body: "Customers never get a better experience than employees do. I've hired, trained, mentored, and led teams — and learned that performance, culture, and communication rise or fall on whether people feel seen, supported, and trusted.",
    items: ["Hiring & training", "Team development", "Mentoring", "Leadership", "Employee satisfaction", "Culture", "Performance", "Communication", "Developing people"],
    metric: null,
  },
  {
    id: "sales-marketing",
    title: "Sales & Marketing",
    body: "Sales taught me how people make decisions; marketing taught me how people connect with ideas. Together they're a masterclass in persuasion, customer psychology, positioning, and relationship-building — and the reason I never confuse attention with trust.",
    items: ["Persuasion", "Customer psychology", "Communication", "Relationship-building", "Revenue", "Positioning", "Customer behavior"],
    metric: null,
  },
  {
    id: "operations",
    title: "Operations",
    body: "Systems either support people or make their lives unnecessarily hard. I've built workflows, structures, and processes that scale — and untangled plenty that didn't. Good operations is invisible; bad operations is everyone's problem by Tuesday.",
    items: ["Workflow", "Systems", "Efficiency", "Organizational structure", "Team processes", "Scaling", "Problem solving"],
    metric: null,
  },
  {
    id: "coaching",
    title: "Coaching",
    body: "Formal ontological coach training, plus one-on-one client work since 2019. Coaching gave me an additional lens for understanding behavior, communication, assumptions, identity, emotions, and decision-making — and a way of working that changes not just what people do, but how they see themselves while they do it.",
    items: ["Behavior", "Communication", "Assumptions", "Identity", "Emotions", "Decision-making", "Leadership", "Relationships"],
    metric: null,
  },
];

const ROLES = [
  "Chief Experience Officer", "VP Customer Experience", "Head of Customer Experience",
  "Employee Experience", "People & Culture", "Organizational Development",
  "Leadership Development", "Strategic Operations", "Fractional leadership", "Consulting",
];

const Experience = () => (
  <>
    <section data-testid="experience-hero" className="py-20 sm:py-28">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <Overline>Experience</Overline>
        <h1 className="mt-6 font-serif font-medium tracking-tight leading-[1.05] text-ink text-5xl sm:text-6xl lg:text-7xl max-w-4xl">
          <MaskLine>I know people.</MaskLine>
          <MaskLine delay={0.15}>
            I also know <em className="text-clay">business.</em>
          </MaskLine>
        </h1>
        <FadeUp delay={0.3}>
          <p className="mt-8 max-w-2xl text-base sm:text-lg leading-relaxed text-mute">
            More than two decades working across sales, marketing, operations, customer experience,
            employee experience, business growth, leadership, and coaching.
          </p>
        </FadeUp>
      </div>
    </section>

    <section data-testid="experience-metrics" className="border-y border-soot/10 bg-cream">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-10 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-8">
        {METRICS.map((m, i) => (
          <FadeUp key={m.value} delay={0.05 * i}>
            <div data-testid={`experience-metric-${i}`}>
              <p className="font-serif text-4xl sm:text-5xl font-medium text-clay leading-none">{m.value}</p>
              <p className="mt-2 text-xs leading-relaxed text-mute">{m.detail}</p>
            </div>
          </FadeUp>
        ))}
      </div>
    </section>

    <section data-testid="expertise-section" className="py-24 sm:py-32">
      <div className="max-w-5xl mx-auto px-5 sm:px-8 space-y-20">
        {EXPERTISE.map((area, i) => (
          <FadeUp key={area.id}>
            <article data-testid={`expertise-${area.id}`} className="grid gap-8 md:grid-cols-[220px_1fr]">
              <div>
                <p className="font-serif italic text-2xl text-clay/70">{String(i + 1).padStart(2, "0")}</p>
                <h2 className="mt-2 font-serif text-3xl sm:text-4xl font-medium tracking-tight text-ink leading-[1.12]">
                  {area.title}
                </h2>
              </div>
              <div className="border-t md:border-t-0 md:border-l border-soot/10 pt-6 md:pt-0 md:pl-10">
                <p className="text-base sm:text-lg leading-relaxed text-mute">{area.body}</p>
                <ul className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2.5">
                  {area.items.map((item) => (
                    <li key={item} className="flex items-start gap-2.5 text-sm text-soot/80">
                      <span className="mt-[7px] h-1 w-1 shrink-0 rounded-full bg-clay" />
                      {item}
                    </li>
                  ))}
                </ul>
                {area.metric && (
                  <p className="mt-6 bg-blush border-l-2 border-clay p-5 text-sm leading-relaxed text-ink">
                    {area.metric}
                  </p>
                )}
              </div>
            </article>
          </FadeUp>
        ))}
      </div>
    </section>

    <section data-testid="recruiter-section" className="bg-ink text-[#F5F5F7] py-24 sm:py-32">
      <div className="max-w-4xl mx-auto px-5 sm:px-8">
        <FadeUp>
          <Overline dark>For Recruiters &amp; Headhunters</Overline>
          <h2 className="mt-5 font-serif text-4xl sm:text-5xl font-medium tracking-tight leading-[1.1]">
            Looking for a <em className="text-clay">people-and-experience</em> leader?
          </h2>
        </FadeUp>
        <FadeUp delay={0.12} className="mt-10 space-y-5 text-base sm:text-lg leading-relaxed text-[#F5F5F7]/70">
          <p>
            Jocelyn's career sits at an unusual intersection:{" "}
            <span className="text-[#F5F5F7] font-semibold">
              Customer Experience + Employee Experience + Leadership + Operations + Revenue + Coaching.
            </span>
          </p>
          <p>
            She understands customers. She understands employees. She understands systems. She understands
            business. Most importantly, she understands how all of those things affect one another.
          </p>
        </FadeUp>
        <FadeUp delay={0.18}>
          <p className="mt-10 text-[11px] font-sans font-bold uppercase tracking-[0.25em] text-clay">
            Areas &amp; roles of interest
          </p>
          <div className="mt-4 flex flex-wrap gap-2.5">
            {ROLES.map((role) => (
              <span
                key={role}
                className="rounded-full border border-white/15 px-4 py-2 text-xs text-[#F5F5F7]/80"
              >
                {role}
              </span>
            ))}
          </div>
        </FadeUp>
        <FadeUp delay={0.24} className="mt-12 flex flex-wrap gap-4">
          <Link
            to="/contact"
            data-testid="recruiter-cta-contact"
            className="inline-flex items-center gap-2 rounded-full bg-clay px-7 py-3.5 text-xs font-sans font-bold uppercase tracking-[0.2em] text-bone transition-colors duration-300 hover:bg-claydark"
          >
            Contact Jocelyn <ArrowRight className="h-4 w-4" />
          </Link>
        </FadeUp>
      </div>
    </section>
  </>
);

export default Experience;
